import type { ReactNode } from 'react';
import { useGetIdentity, useLogout } from '@refinedev/core';
import { useLocation } from 'react-router';
import { LogOut, Menu, Moon, PanelLeftClose, PanelLeftOpen, Sun } from 'lucide-react';
import { resources } from '@/resources';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { Sidebar } from './Sidebar';
import { SidebarStateProvider, useSidebarState } from './sidebar-state';
import { extensionDisplayName } from '@/extensions/extension-nav';

// Admin chrome: full-height sidebar (brand + nav + identity) with a slim
// context bar over the content. The shared <TrackframeHeader /> stays on the
// hub root (HubLayout); the admin subtree owns its own header so it can be
// fully token-themed.
//
// md+ shows the expanded sidebar or, collapsed, the icon rail (⌘B); below md
// the sidebar is a drawer opened from the top bar.
export function AdminLayout({ children }: { children: ReactNode }) {
  return (
    <SidebarStateProvider>
      <AdminShell>{children}</AdminShell>
    </SidebarStateProvider>
  );
}

function AdminShell({ children }: { children: ReactNode }) {
  const { collapsed, drawerOpen, closeDrawer } = useSidebarState();
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground">
      <div className="hidden h-full shrink-0 md:flex">
        <Sidebar variant={collapsed ? 'rail' : 'expanded'} />
      </div>
      {drawerOpen && (
        <div className="md:hidden">
          <div className="fixed inset-0 z-40 bg-black/40" aria-hidden="true" onClick={closeDrawer} />
          <Sidebar variant="drawer" />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
}

const ACTION_LABELS: Record<string, string> = { show: 'Details', edit: 'Edit', create: 'New' };

function breadcrumb(pathname: string): string[] {
  const segments = pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
  if (segments.length === 0) return ['Dashboard'];
  // HUB-103 — /admin/ext/<name>[/settings] is not a refine resource.
  if (segments[0] === 'ext' && segments[1]) {
    return ['Extensions', extensionDisplayName(segments[1]), ...(segments[2] === 'settings' ? ['Settings'] : [])];
  }
  const resource = resources.find((r) => r.name === segments[0]);
  const parts = [String(resource?.meta?.label ?? segments[0])];
  if (segments[1] && ACTION_LABELS[segments[1]]) parts.push(ACTION_LABELS[segments[1]]);
  return parts;
}

const ICON_BUTTON =
  'flex cursor-pointer items-center justify-center rounded-[var(--radius-ctl)] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function TopBar() {
  const location = useLocation();
  const { mode, setMode } = useTheme();
  const { data: identity } = useGetIdentity<{ email?: string; name?: string | null }>();
  const { mutate: logout } = useLogout();
  const { collapsed, toggleCollapsed, openDrawer } = useSidebarState();
  const parts = breadcrumb(location.pathname);
  const toggleLabel = collapsed ? 'Expand sidebar (⌘B)' : 'Collapse sidebar (⌘B)';

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b bg-card px-3 md:px-5">
      <button
        type="button"
        className={cn(ICON_BUTTON, 'hidden size-8 md:flex')}
        onClick={toggleCollapsed}
        aria-label={toggleLabel}
        title={toggleLabel}
      >
        {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
      </button>
      <button
        type="button"
        className={cn(ICON_BUTTON, 'size-11 md:hidden')}
        onClick={openDrawer}
        aria-label="Open menu"
      >
        <Menu className="size-5" />
      </button>
      <span className="min-w-0 truncate text-base font-semibold md:hidden">{parts[parts.length - 1]}</span>
      <nav aria-label="Breadcrumb" className="hidden min-w-0 truncate text-sm text-muted-foreground md:block">
        {parts.map((part, i) => (
          <span key={part}>
            {i > 0 && <span className="mx-1.5 text-muted-foreground/60">/</span>}
            <span className={i === parts.length - 1 ? 'font-semibold text-foreground' : undefined}>
              {part}
            </span>
          </span>
        ))}
      </nav>
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          className={cn(ICON_BUTTON, 'size-8')}
          onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
          aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {mode === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
        {identity?.email && (
          <span className="hidden max-w-56 truncate px-2 text-xs text-muted-foreground sm:block">
            {identity.name || identity.email}
          </span>
        )}
        <button
          type="button"
          className={cn(ICON_BUTTON, 'size-8')}
          onClick={() => logout()}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </header>
  );
}
